import { createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities, getListingsById } from '../../ducks/marketplaceData.duck';
import { parse } from '../../util/urlHelpers';
import { catalogSearchListings, useFirebaseCatalog } from '../../util/api';
import { DEFAULT_SEO_LOCATION_PAGE_ID, getSeoLocationPageConfig } from './SeoLocationPage.config';

const RESULT_PAGE_SIZE = 24;
const SEO_CARD_IMAGE_VARIANT = 'seo-card';
const SEO_CARD_IMAGE_VARIANT_2X = 'seo-card-2x';

// ================ Action types ================ //

const FETCH_PORT_CREDIT_LISTINGS_REQUEST = 'app/SeoLocationPage/FETCH_PORT_CREDIT_LISTINGS_REQUEST';
const FETCH_PORT_CREDIT_LISTINGS_SUCCESS = 'app/SeoLocationPage/FETCH_PORT_CREDIT_LISTINGS_SUCCESS';
const FETCH_PORT_CREDIT_LISTINGS_ERROR = 'app/SeoLocationPage/FETCH_PORT_CREDIT_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  inProgress: false,
  error: null,
  resultIds: [],
  pagination: null,
};

const resultIds = response =>
  response.data.data
    .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
    .map(l => l.id);

export default function seoLocationPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_PORT_CREDIT_LISTINGS_REQUEST:
      return { ...state, inProgress: true, error: null };

    case FETCH_PORT_CREDIT_LISTINGS_SUCCESS:
      return {
        ...state,
        inProgress: false,
        resultIds: resultIds(payload.response),
        pagination: payload.response.data.meta,
      };

    case FETCH_PORT_CREDIT_LISTINGS_ERROR:
      return { ...state, inProgress: false, error: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchPortCreditListingsRequest = () => ({
  type: FETCH_PORT_CREDIT_LISTINGS_REQUEST,
});

const fetchPortCreditListingsSuccess = response => ({
  type: FETCH_PORT_CREDIT_LISTINGS_SUCCESS,
  payload: { response },
});

const fetchPortCreditListingsError = e => ({
  type: FETCH_PORT_CREDIT_LISTINGS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

const imageParams = () => ({
  'fields.image': [
    'variants.scaled-small',
    'variants.scaled-medium',
    'variants.listing-card',
    'variants.listing-card-2x',
    `variants.${SEO_CARD_IMAGE_VARIANT}`,
    `variants.${SEO_CARD_IMAGE_VARIANT_2X}`,
    'variants.facebook',
    'variants.twitter',
  ],
  ...createImageVariantConfig('listing-card', 400, 1),
  ...createImageVariantConfig('listing-card-2x', 800, 1),
  ...createImageVariantConfig(SEO_CARD_IMAGE_VARIANT, 248, 1),
  ...createImageVariantConfig(SEO_CARD_IMAGE_VARIANT_2X, 496, 1),
  ...createImageVariantConfig('facebook', 1200, 630 / 1200),
  ...createImageVariantConfig('twitter', 600, 314 / 600),
  'limit.images': 1,
});

const searchParams = pageConfig => {
  const apiSearchParams = parse(pageConfig.searchQuery, {
    latlngBounds: ['bounds'],
  });
  delete apiSearchParams.mapSearch;

  return apiSearchParams;
};

const queryProducts = (sdk, pageConfig) => {
  if (useFirebaseCatalog()) {
    const { keywords, pub_categoryLevel1, authorId } = searchParams(pageConfig);
    return catalogSearchListings({
      keywords,
      pub_categoryLevel1,
      authorId,
      perPage: RESULT_PAGE_SIZE,
    });
  }

  return sdk.listings.query({
    ...searchParams(pageConfig),
    pub_listingType: 'sell-new-products',
    page: 1,
    perPage: RESULT_PAGE_SIZE,
    minStock: 1,
    stockMode: 'match-undefined',
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.listing': [
      'title',
      'price',
      'deleted',
      'state',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.cardStyle',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
      'publicData.priceVariationsEnabled',
      'publicData.priceVariants',
      'publicData.aggregateRating',
      'publicData.numberOfRatings',
      'publicData.videos',
      'publicData.ready_for_sale',
      'publicData.preparationHours',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    ...imageParams(),
  });
};

export const fetchPortCreditListings = config => (dispatch, getState, sdk) => {
  const pageConfig = getSeoLocationPageConfig(config?.seoPageId || DEFAULT_SEO_LOCATION_PAGE_ID);

  if (pageConfig.hideProducts) {
    dispatch(fetchPortCreditListingsSuccess({ data: { data: [], meta: null } }));
    return Promise.resolve();
  }

  dispatch(fetchPortCreditListingsRequest());

  return queryProducts(sdk, pageConfig)
    .then(response => {
      const sanitizeConfig = { listingFields: config?.listing?.listingFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(fetchPortCreditListingsSuccess(response));
      return response;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(fetchPortCreditListingsError(error));
      throw e;
    });
};

export const loadData = (params, search, config) => dispatch =>
  dispatch(
    fetchPortCreditListings({
      ...config,
      seoPageId: params?.seoPageId || DEFAULT_SEO_LOCATION_PAGE_ID,
    })
  );

// ================ Selectors ================ //

export const portCreditListingsSelector = state =>
  getListingsById(state, state.SeoLocationPage.resultIds || []) || [];

export const portCreditInProgressSelector = state => state.SeoLocationPage.inProgress;
export const portCreditErrorSelector = state => state.SeoLocationPage.error;
